import { Injectable } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { GroupedDataSet, Event } from '../../types';

function getByPath(obj: unknown, path: string): unknown {
  const segments = path.split('.');

  function traverse(current: unknown, index: number): unknown {
    if (index >= segments.length) return current;

    const segment = segments[index];

    const arrayMatch = segment.match(/^(.*?)\[(\*|\d+)\]$/);
    if (arrayMatch) {
      const [, key, sub] = arrayMatch;

      const container = key ? (current as any)?.[key] : current;

      if (!Array.isArray(container)) return undefined;

      if (sub === '*') {
        return container.map((item) => traverse(item, index + 1)).flat();
      } else {
        const idx = parseInt(sub, 10);
        return traverse(container[idx], index + 1);
      }
    }

    return traverse((current as any)?.[segment], index + 1);
  }

  return traverse(obj, 0);
}

@Injectable({
  providedIn: 'root',
})
export class SharedFunctionalityService {
  constructor() {}
  /**
   * Generic search filter function
   * @param searchValue The search query string
   * @param allItems The full list of items (can be Templates, Events, etc.)
   * @param key The list of key to search on (e.g., 'templateName' or 'storeId')
   * @param changeDetectorRef Reference to trigger change detection
   * @returns Filtered items and the toggle state
   */
  applySearchFilter<T>(
    searchValue: string,
    allItems: T[],
    keys: Array<string>,
    changeDetectorRef: ChangeDetectorRef,
    slideToggleState: boolean
  ): { filteredItems: T[]; slideToggleState: boolean } {
    searchValue = searchValue.trim().toLowerCase();
    let filteredItems: T[] = [];

    if (searchValue !== '') {
      filteredItems = allItems.filter((item: T) => {
        return keys.some((key) => {
          const value = getByPath(item, key);
          if (!value) {
            return false;
          }
          if (Array.isArray(value)) {
            return value.some((v) => {
              return String(v).toLowerCase().includes(searchValue);
            });
          } else {
            return String(value).toLowerCase().includes(searchValue);
          }
        });
      });
      slideToggleState = false;
    } else {
      filteredItems = [...allItems];
      slideToggleState = true;
    }

    changeDetectorRef.detectChanges();
    return { filteredItems, slideToggleState };
  }

  /**
   * Generic toggle function for slide toggles
   */
  onSlideToggleChange(slideToggleState: boolean): boolean {
    return !slideToggleState;
  }

  /**
   * Generic filter change function
   * @param filters The list of filters
   * @param allItemsAccordion The accordion data (any structure)
   * @param key The key to match filters with
   * @returns Filtered accordion data
   */
  onFilterChange<T>(
    filters: { text: string; checked: boolean }[],
    allItemsAccordion: any,
    key: keyof T
  ): any {
    const activeFilters = filters
      .filter((f) => f.checked)
      .map((f) => f.text.toLowerCase());

    return {
      ...allItemsAccordion,
      accordion: allItemsAccordion.accordion.filter((accordion: T) =>
        activeFilters.includes(String(accordion[key]).toLowerCase())
      ),
    };
  }

  getGroupedDataSet<T>(
    entities: T[],
    groups: { id: string; label: string }[],
    groupAccessor: (entity: T) => string
  ): GroupedDataSet<T> {
    const groupObj: { [key: string]: T[] } = {};

    entities.forEach((entity) => {
      const groupKey = groupAccessor(entity);
      if (!groupObj[groupKey]) {
        groupObj[groupKey] = [];
      }
      groupObj[groupKey].push(entity);
    });

    const groupArray = groups.map((group) => {
      return {
        id: group.id,
        label: group.label,
        entities: groupObj[group.id] || [],
      };
    });

    return { groups: groupArray };
  }

  findEventById(eventId: string, events: Event[]): Event | null {
    const event = events.find((event) => event.id === eventId);
    return event ? event : null;
  }
}
